import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { roleLabel, roleColor, situacaoEleitLabel, situacaoEleitColor,
         validacaoWALabel, validacaoWAColor } from '../utils/helpers';
import AppShell    from '../components/AppShell';
import KPICard     from '../components/KPICard';
import Pill        from '../components/Pill';
import TreeNode    from '../components/TreeNode';
import EditModal   from '../components/EditModal';
import BotWhatsApp from '../components/BotWhatsApp';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
         PieChart, Pie, Cell, Legend } from 'recharts';

const TABS = [
  { id: 'dashboard', label: '📊 Dashboard'        },
  { id: 'usuarios',  label: '👥 Usuários'          },
  { id: 'rede',      label: '🔺 Rede Hierárquica' },
  { id: 'bot',       label: '🤖 Bot WhatsApp'      },
];

const TOOLTIP_STYLE = { background: '#fff', border: '1px solid var(--primary)', borderRadius: 8, color: '#000' };

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const [tab,        setTab]        = useState('dashboard');
  const [stats,      setStats]      = useState(null);
  const [users,      setUsers]      = useState([]);
  const [tree,       setTree]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [inviteLink, setInviteLink] = useState('');
  const [editUser,   setEditUser]   = useState(null);

  // Filtros
  const [search,       setSearch]       = useState('');
  const [filterMun,    setFilterMun]    = useState('');
  const [filterRole,   setFilterRole]   = useState('');
  const [filterSitEl,  setFilterSitEl]  = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sR, uR, tR] = await Promise.all([
        axios.get('/api/users/dashboard'),
        axios.get('/api/users'),
        axios.get('/api/users/tree'),
      ]);
      setStats(sR.data); setUsers(uR.data); setTree(tR.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateSitEleit = async (id, val) => {
    await axios.patch(`/api/users/${id}/situacao-eleitoral`, { situacao_eleitoral: val });
    load();
  };
  const getInviteLink = async () => {
    const r = await axios.get(`/api/users/${user.id}/invite-link`);
    setInviteLink(r.data.link);
  };

  const uniqueMunicipios = [...new Set(users.map(u => u.municipio).filter(Boolean))].sort();
  const filtered = users.filter(u =>
    (!search      || u.nome?.includes(search.toUpperCase()) || u.login?.includes(search)) &&
    (!filterMun   || u.municipio === filterMun) &&
    (!filterRole  || u.role      === filterRole) &&
    (!filterSitEl || u.situacao_eleitoral === filterSitEl)
  );

  const porRoleData = stats?.porRole?.map(r => ({
    name:  roleLabel(r.role).replace(/^.+? /, ''),
    value: parseInt(r.total),
    color: roleColor(r.role),
  })) || [];

  const porSitData = stats?.porSituacaoEleitoral?.map(s => ({
    name:  { EM_ANALISE: 'Em Análise', IRREGULAR: 'Irregular', ATIVO: 'Ativo' }[s.situacao_eleitoral] || s.situacao_eleitoral,
    value: parseInt(s.total),
    color: situacaoEleitColor(s.situacao_eleitoral),
  })) || [];

  const sideBottom = (
    <>
      <button onClick={getInviteLink} className="btn btn-light btn-sm" style={{ width: '100%' }}>
        🔗 Meu Link de Convite
      </button>
      {inviteLink && (
        <div className="invite-box">
          <input readOnly value={inviteLink} className="invite-input"
            onClick={e => { e.target.select(); navigator.clipboard.writeText(inviteLink); }} />
          <button onClick={() => navigator.clipboard.writeText(inviteLink)} className="copy-btn">📋</button>
        </div>
      )}
      <button onClick={logout} className="btn btn-outline btn-sm"
        style={{ width: '100%', borderColor: 'var(--danger)', color: 'var(--danger)' }}>Sair</button>
    </>
  );

  const kpiRole = (role) => parseInt(stats?.porRole?.find(r => r.role === role)?.total || 0);
  const kpiCad  = (sit)  => parseInt(stats?.porCadastro?.find(s => s.situacao_cadastro === sit)?.total || 0);
  const kpiEl   = (sit)  => parseInt(stats?.porSituacaoEleitoral?.find(s => s.situacao_eleitoral === sit)?.total || 0);
  const kpiWA   = (sit)  => parseInt(stats?.porValidacaoWA?.find(s => s.validacao_whatsapp === sit)?.total || 0);

  return (
    <AppShell
      tabs={TABS}
      activeTab={tab}
      onTabChange={setTab}
      roleBadge="🔵 COORD. GERAL"
      userName={user?.nome}
      sideBottom={sideBottom}
    >
      {loading && <div className="loading-screen"><div className="spinner" /> Carregando...</div>}

      {/* ── DASHBOARD ── */}
      {!loading && tab === 'dashboard' && stats && (
        <div>
          <h2 className="page-title">
            Dashboard — Minha Rede
            <span style={{ fontSize: 13, color: '#555', fontWeight: 400, marginLeft: 12 }}>
              {stats.total} pessoas na sua hierarquia
            </span>
          </h2>

          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', letterSpacing: 1, marginBottom: 8 }}>DISTRIBUIÇÃO POR PERFIL</div>
          <div className="kpi-grid" style={{ marginBottom: 14 }}>
            <KPICard label="Total na Rede"     value={stats.total}        icon="👥" color="#000080" />
            <KPICard label="Coord. Municipais" value={kpiRole('COORDENADOR_MUNICIPAL')} icon="🟢" color="#057a55" />
            <KPICard label="Lideranças"        value={kpiRole('LIDERANCA')} icon="🟡" color="#c27803" />
            <KPICard label="Apoiadores"        value={kpiRole('APOIADOR')}  icon="⚪" color="#6b7280" />
          </div>

          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', letterSpacing: 1, marginBottom: 8 }}>SITUAÇÃO DOS CADASTROS</div>
          <div className="kpi-grid" style={{ marginBottom: 14 }}>
            <KPICard label="Cadastros Ativos"     value={kpiCad('ATIVO')}     icon="✅" color="#057a55" />
            <KPICard label="Cadastros Pendentes"  value={kpiCad('PENDENTE')}  icon="⏳" color="#c27803" />
            <KPICard label="Sit. Eleit. Ativo"    value={kpiEl('ATIVO')}      icon="🗳️" color="#1a56db" />
            <KPICard label="Sit. Eleit. Irregular" value={kpiEl('IRREGULAR')} icon="⚠️" color="#cc0000" />
          </div>

          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', letterSpacing: 1, marginBottom: 8 }}>VALIDAÇÃO WHATSAPP</div>
          <div className="kpi-grid" style={{ marginBottom: 20 }}>
            <KPICard label="WhatsApp Validado"   value={kpiWA('ATIVO')}        icon="✅" color="#057a55" />
            <KPICard label="Não Validado"         value={kpiWA('NAO_VALIDADO')} icon="❌" color="#cc0000" />
            <KPICard label="Aguardando"           value={kpiWA('INATIVO')}      icon="⬜" color="#6b7280" />
          </div>

          <div className="charts-grid">
            <div className="card">
              <h3 className="card-title">Distribuição por Perfil</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={porRoleData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                    label={({ name, value }) => `${name}: ${value}`}>
                    {porRoleData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="card">
              <h3 className="card-title">Situação Eleitoral</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={porSitData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                    label={({ name, value }) => `${name}: ${value}`}>
                    {porSitData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card">
            <h3 className="card-title">Municípios — Minha Rede</h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={stats.porMunicipio?.slice(0, 15).map(m => ({ name: m.municipio, total: parseInt(m.total) }))}>
                <XAxis dataKey="name" stroke="var(--primary)" tick={{ fontSize: 11 }} />
                <YAxis stroke="var(--primary)" tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="total" fill="var(--primary)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── USUÁRIOS ── */}
      {!loading && tab === 'usuarios' && (
        <div>
          <h2 className="page-title">Usuários <span style={{ fontSize: 15, color: 'var(--primary)' }}>({filtered.length})</span></h2>

          {editUser && (
            <EditModal user={editUser} onClose={() => setEditUser(null)}
              onSaved={() => { setEditUser(null); load(); }} />
          )}

          <div className="filter-bar">
            <input className="r-input" placeholder="Buscar nome ou login..." value={search} onChange={e => setSearch(e.target.value)} />
            <select className="r-select" value={filterMun} onChange={e => setFilterMun(e.target.value)}>
              <option value="">Todos municípios</option>
              {uniqueMunicipios.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <select className="r-select" value={filterRole} onChange={e => setFilterRole(e.target.value)}>
              <option value="">Todos perfis</option>
              <option value="COORDENADOR_MUNICIPAL">Coord. Municipal</option>
              <option value="LIDERANCA">Liderança</option>
              <option value="APOIADOR">Apoiador</option>
            </select>
            <select className="r-select" value={filterSitEl} onChange={e => setFilterSitEl(e.target.value)}>
              <option value="">Situação Eleitoral</option>
              <option value="EM_ANALISE">Em Análise</option>
              <option value="IRREGULAR">Irregular</option>
              <option value="ATIVO">Ativo</option>
            </select>
          </div>

          <div className="table-wrap">
            <table className="r-table">
              <thead>
                <tr>
                  {['NOME','CPF','MUNICÍPIO','PERFIL','SIT. ELEITORAL','VALID. WA','AÇÕES'].map(h => <th key={h}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.id}>
                    <td><div style={{ fontWeight: 600 }}>{u.nome || '-'}</div><div style={{ fontSize: 11, color: '#555' }}>{u.login}</div></td>
                    <td>{u.cpf || '-'}</td>
                    <td>{u.municipio || '-'}</td>
                    <td><Pill label={roleLabel(u.role)} color={roleColor(u.role)} /></td>
                    <td><Pill label={situacaoEleitLabel(u.situacao_eleitoral)} color={situacaoEleitColor(u.situacao_eleitoral)} /></td>
                    <td><Pill label={validacaoWALabel(u.validacao_whatsapp)} color={validacaoWAColor(u.validacao_whatsapp)} /></td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <button onClick={() => setEditUser(u)} className="btn btn-sm btn-light">✏️ Editar</button>
                        <select onChange={e => { if (e.target.value) { updateSitEleit(u.id, e.target.value); e.target.value = ''; } }}
                          className="r-select" style={{ fontSize: 11, padding: '4px 6px' }} defaultValue="">
                          <option value="" disabled>Sit. Eleitoral...</option>
                          <option value="EM_ANALISE">Em Análise</option>
                          <option value="IRREGULAR">Irregular</option>
                          <option value="ATIVO">Ativo</option>
                        </select>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── REDE ── */}
      {!loading && tab === 'rede' && (
        <div>
          <h2 className="page-title">Rede Hierárquica</h2>
          <div className="tree-container">
            {tree.map(node => <TreeNode key={node.id} node={node} level={0} />)}
          </div>
        </div>
      )}

      {/* ── BOT ── */}
      {tab === 'bot' && (
        <BotWhatsApp users={users} coordId={user?.id} />
      )}
    </AppShell>
  );
}
